import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { URLS } from '@shared/constants';
import {
  CommonResponseDTO,
  IMailboxUserSettingsConfig,
} from '@shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class MailboxApiService {
  constructor(private readonly http: HttpClient) {}

  private readonly _dataStore = new BehaviorSubject<
    IMailboxUserSettingsConfig[]
  >([]);
  public dataStore = this._dataStore.asObservable();
  private readonly _isLoginComplete = new BehaviorSubject<boolean>(false);
  public isLoginComplete = this._isLoginComplete.asObservable();
  private readonly apiUrl = 'https://mailbridge.echonlabs.com';
  // private readonly apiUrl = 'http://localhost:8000';

  private readonly _activeUser =
    new BehaviorSubject<IMailboxUserSettingsConfig>(undefined);
  public activeUser = this._activeUser.asObservable();

  getCreatedAccounts(): Observable<
    CommonResponseDTO<IMailboxUserSettingsConfig[]>
  > {
    return this.http
      .get<CommonResponseDTO<IMailboxUserSettingsConfig[]>>(
        URLS.MAILBOX_ACCOUNTS
      )
      .pipe(
        switchMap((res) => {
          if (res.data.length === 0) this._isLoginComplete.next(true);
          // Process each account in parallel (validate + configure)
          const accountRequests: Observable<IMailboxUserSettingsConfig>[] =
            res.data.map((account) => this.processAccount(account));

          return forkJoin(accountRequests).pipe(
            map((accounts) => ({
              statusCode: res.statusCode, // Preserve original response properties
              message: 'Accounts processed successfully',
              success: true,
              data: accounts,
            }))
          );
        }),
        tap((response) => {
          this._dataStore.next(response.data);
          const activeUser = response.data.find((item) => item.isCurrent);
          this._activeUser.next(activeUser);
          this.loginToAccount(activeUser).subscribe();
        })
      );
  }

  private processAccount(
    account: IMailboxUserSettingsConfig
  ): Observable<IMailboxUserSettingsConfig> {
    const requestBody = {
      email: account.imapConfig.username,
      smtp_port: account.smtpConfig.port,
      imap_port: account.imapConfig.port,
      imap_server: account.imapConfig.host,
      smtp_server: account.smtpConfig.host,
      password: account.imapConfig.password,
    };

    return forkJoin({
      validation: this.http
        .post<{ message: string }>(
          `${this.apiUrl}/api/v1/auth/validate`,
          requestBody
        )
        .pipe(map((res) => res.message === 'Mailbox connection is valid')),
    }).pipe(
      map(({ validation }) => ({
        ...account,
        isValidConnection: validation,
      }))
    );
  }

  loginToAccount(
    account
  ): Observable<{ jwt_token: string; refresh_token: string }> {
    const requestBody = {
      email: account.imapConfig.username,
      imap_server: account.imapConfig.host,
      smtp_server: account.smtpConfig.host,
      imap_port: account.imapConfig.port,
      smtp_port: account.smtpConfig.port,
      password: account.imapConfig.password,
    };

    return this.http
      .post<{ jwt_token: string; refresh_token: string }>(
        `${this.apiUrl}/api/v1/auth/login`,
        requestBody
      )
      .pipe(
        tap((res) => {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.setItem('accessToken', res.jwt_token);
          localStorage.setItem('refreshToken', res.refresh_token);
          this._isLoginComplete.next(true);
        })
      );
  }

  initMailboxConfigs(): Observable<
    CommonResponseDTO<IMailboxUserSettingsConfig[]>
  > {
    return this.http
      .get<CommonResponseDTO<IMailboxUserSettingsConfig[]>>(URLS.MAILBOX)
      .pipe(
        tap((res) => {
          this._dataStore.next(res.data);
          const activeUser = res.data.find((item) => item.isCurrent);

          this._activeUser.next(activeUser);
        })
      );
  }

  getAllConfigs(): Observable<CommonResponseDTO<IMailboxUserSettingsConfig[]>> {
    return this.http.get<CommonResponseDTO<IMailboxUserSettingsConfig[]>>(
      URLS.MAILBOX_SETTINGS
    );
  }

  syncConnection(
    id: string
  ): Observable<CommonResponseDTO<IMailboxUserSettingsConfig>> {
    const url = `${URLS.MAILBOX_SETTINGS}/${id}/sync`;
    return this.http
      .get<CommonResponseDTO<IMailboxUserSettingsConfig>>(url)
      .pipe(
        tap((res) => {
          const currentData = this._dataStore.getValue();
          if (currentData.length > 0) {
            const currentAccountIndex = currentData.findIndex(
              (item) => item._id.toString() === res.data._id.toString()
            );
            currentData[currentAccountIndex].isValidConnection =
              res.data.isValidConnection;
            this._dataStore.next([...currentData]);
          } else {
            this._dataStore.next([res.data]);
          }
        })
      );
  }

  createConfig(
    data: IMailboxUserSettingsConfig
  ): Observable<CommonResponseDTO<IMailboxUserSettingsConfig>> {
    const requestBody = {
      email: data.imapConfig.username,
      imap_server: data.imapConfig.host,
      smtp_server: data.smtpConfig.host,
      imap_port: data.imapConfig.port,
      smtp_port: data.smtpConfig.port,
      password: data.imapConfig.password,
    };

    return this.http
      .post<{ message: string }>(
        `${this.apiUrl}/api/v1/auth/validate`,
        requestBody
      )
      .pipe(
        tap((res) => {
          if (res.message === 'Mailbox connection is valid') {
            data = { ...data, isValidConnection: true };
          } else {
            data = { ...data, isValidConnection: false };
          }
        }),
        switchMap(() =>
          this.http
            .post<CommonResponseDTO<IMailboxUserSettingsConfig>>(
              URLS.MAILBOX_SETTINGS,
              data
            )
            .pipe(
              tap((res) => {
                const currentData = this._dataStore.getValue();
                this._activeUser.next(res.data);
                this.loginToAccount(res.data).subscribe();

                if (currentData.length > 0) {
                  const currentAccountIndex = currentData.findIndex(
                    (item) => item.isCurrent === true
                  );
                  if (currentAccountIndex !== -1) {
                    currentData[currentAccountIndex].isCurrent = false;
                  }
                  this._dataStore.next([...currentData, res.data]);
                } else {
                  this._dataStore.next([res.data]);
                }
              })
            )
        ),
        catchError((error) => {
          const errorMessage =
            error?.error?.message ||
            'An error occurred while creating the configuration.';
          return throwError(() => errorMessage);
        })
      );
  }

  updateConfig(
    data: IMailboxUserSettingsConfig
  ): Observable<CommonResponseDTO<IMailboxUserSettingsConfig>> {
    const url = `${URLS.MAILBOX_SETTINGS}/${data._id.toString()}`;
    const requestBody = {
      email: data.imapConfig.username,
      imap_server: data.imapConfig.host,
      smtp_server: data.smtpConfig.host,
      imap_port: data.imapConfig.port,
      smtp_port: data.smtpConfig.port,
      password: data.imapConfig.password,
    };
    return this.http
      .post<{ message: string }>(
        `${this.apiUrl}/api/v1/auth/validate`,
        requestBody
      )
      .pipe(
        tap((res) => {
          if (res.message === 'Mailbox connection is valid') {
            data = { ...data, isValidConnection: true };
          } else {
            data = { ...data, isValidConnection: false };
          }
        }),
        switchMap(() =>
          this.http
            .patch<CommonResponseDTO<IMailboxUserSettingsConfig>>(url, data)
            .pipe(
              tap((res) => {
                let currentData = this._dataStore.getValue();
                const index = currentData.findIndex(
                  (item) => item._id.toString() === data._id.toString()
                );

                if (index !== -1) {
                  currentData[index] = res.data;
                } else {
                  currentData = [...currentData, res.data];
                }
                this._dataStore.next([...currentData]);
                if (res.data.isCurrent) {
                  this._activeUser.next(res.data);
                  this.loginToAccount(res.data).subscribe();
                }
              })
            )
        ),
        catchError((error) => {
          const errorMessage =
            error?.error?.message ||
            'An error occurred while creating the configuration.';
          return throwError(() => errorMessage);
        })
      );
  }

  deleteConfig(
    id: string
  ): Observable<CommonResponseDTO<IMailboxUserSettingsConfig[]>> {
    const url = `${URLS.MAILBOX_SETTINGS}/${id}`;
    return this.http
      .delete<CommonResponseDTO<IMailboxUserSettingsConfig[]>>(url)
      .pipe(
        tap((res) => {
          if (res.data.length > 0) {
            const currentData = this._dataStore.getValue();
            const changedActiveUser: IMailboxUserSettingsConfig = res.data.find(
              (item) => {
                return item.isCurrent;
              }
            );
            const index = currentData.findIndex((item) => item._id === id);

            if (index > -1) {
              currentData.splice(index, 1);
            }

            this._activeUser.next(changedActiveUser);

            if (changedActiveUser) {
              this.loginToAccount(changedActiveUser).subscribe();
            }

            if (currentData.length > 0) {
              this._dataStore.next([...currentData]);
            } else {
              this._dataStore.next([]);
              this._activeUser.next(undefined);
            }
          } else {
            this._dataStore.next([]);
            this._activeUser.next(undefined);
          }
        })
      );
  }
}
